import React from "react";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  LinkBox,
  LinkOverlay,
  Tag,
  Text,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FiAward } from "react-icons/fi";
import { expertiseLabel } from "../../constants/expertise";

/**
 * The rail's advertisement for the Expertise tool.
 *
 * Small on purpose — it is a trailer, not the feature. The headline asks
 * "Academic?" because the first vocabulary is academic and it is better to say
 * so than to have a winemaker open a hundred research methods and bounce; the tool
 * itself is open to everyone and says as much on its first screen.
 *
 * Once the member has answered, the card stops selling and starts reporting:
 * their own expertise, and a way back in to change it.
 */
export default function ExpertiseCard({
  cardBg,
  cardText,
  cardTitle,
  cardMuted,
  skillIds = [],
  t = (_key, fallback) => fallback,
}) {
  const hasAnswered = skillIds.length > 0;

  return (
    <LinkBox
      as="article"
      bg={cardBg}
      color={cardText}
      borderRadius="ui-lg"
      boxShadow="ui-sm"
      borderWidth="1px"
      borderColor="border.default"
      p={4}
      transition="all .2s"
      _hover={{ boxShadow: "ui-md", transform: "translateY(-3px)" }}
    >
      <HStack spacing={1.5} mb={1} color="gwbi.600">
        <Icon as={FiAward} boxSize={3.5} />
        <Text
          fontSize="2xs"
          fontWeight="bold"
          letterSpacing="0.16em"
          textTransform="uppercase"
        >
          {t("expertise.eyebrow", "Expertise")}
        </Text>
      </HStack>

      <Heading fontSize="md" lineHeight="1.2" color={cardTitle} mb={1}>
        <LinkOverlay as={RouterLink} to="/expertise">
          {hasAnswered
            ? t("expertise.card_title_done", "Your expertise")
            : t("expertise.card_title", "Academic? Find scholars with your expertise")}
        </LinkOverlay>
      </Heading>

      {hasAnswered ? (
        <Box>
          <Flex wrap="wrap" gap={1} mt={2} mb={2}>
            {skillIds.slice(0, 4).map((id) => (
              <Tag key={id} size="sm" colorScheme="gwbi" variant="subtle">
                {expertiseLabel(id)}
              </Tag>
            ))}
            {skillIds.length > 4 && (
              <Tag size="sm" variant="subtle">
                +{skillIds.length - 4}
              </Tag>
            )}
          </Flex>
          <Text fontSize="sm" fontWeight="semibold" color="gwbi.600">
            {t("expertise.card_edit", "Edit or see matches")} →
          </Text>
        </Box>
      ) : (
        <Box>
          <Text fontSize="xs" color={cardMuted} mb={2}>
            {t(
              "expertise.card_subtitle",
              "Tell us what you can do — methods, not just topics — and we will show you the five members closest to your work. Two minutes."
            )}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="gwbi.600">
            {t("expertise.card_cta", "Start")} →
          </Text>
        </Box>
      )}
    </LinkBox>
  );
}
